import { useMemo } from 'react';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { mapI18nLocalToDateFns } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { GlobalStyles } from 'libs/ui/styled';

import { Icon } from '../Icon';
import { Input, InputProps } from '../Input';

import { DatePickerStyled } from './DatePicker.styles';
import { DatePickerProps } from './types';

const CloseIcon = () => <Icon.Close size={16} />;

/**
 * DatePicker component.
 */
export const DatePicker = ({
  helperText,
  error,
  placeholder,
  readOnly,
  fullWidth,
  showCopyButton,
  name,
  showRequiredMark,
  variant = 'date',
  ...props
}: DatePickerProps) => {
  const { t, language } = useTranslation();

  const adapterLocale = useMemo(
    () => mapI18nLocalToDateFns(language),
    [language]
  );

  return (
    <LocalizationProvider
      adapterLocale={adapterLocale}
      dateAdapter={AdapterDateFns}
    >
      <GlobalStyles
        styles={{
          '.MuiYearCalendar-button, .MuiMonthCalendar-button': {
            textAlign: 'center',
            display: 'inline',
          },
        }}
      />
      <DatePickerStyled
        disableFuture
        closeOnSelect={variant === 'date'}
        disableOpenPicker={readOnly}
        enableAccessibleFieldDOMStructure={false}
        localeText={{ cancelButtonLabel: t('common.cancel') }}
        readOnly={readOnly}
        slotProps={{
          field: { clearable: true },
          textField: {
            helperText,
            name,
            error,
            placeholder,
            fullWidth,
            showCopyButton,
            showRequiredMark: showRequiredMark && !readOnly,
          } as InputProps,
          clearButton: { disableRipple: true },
          openPickerButton: { disableRipple: true },
        }}
        slots={{ textField: Input, clearIcon: CloseIcon }}
        views={variant === 'date' ? ['year', 'month', 'day'] : undefined}
        {...props}
      />
    </LocalizationProvider>
  );
};
